import { useState } from "react";
import { Plus, X, Copy, Cloud, CloudOff, Trash2, AlertTriangle, Circle, Lock, ShieldCheck, History, CheckCircle2, Loader2 } from "lucide-react";
import { Tag } from "./Tag";
import { formatLocalDate } from "../../../../shared/common/datetimeUtils";

function DeleteConfirmModal({ isOpen, scenarioName, onConfirm, onCancel, isDarkMode }) {
  if (!isOpen) return null;

  const overlay = "fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm";
  const modal = isDarkMode
    ? "bg-gray-900 border-gray-700 text-gray-100"
    : "bg-white border-gray-200 text-gray-900";

  return (
    <div className={overlay} onClick={onCancel}>
      <div
        className={`relative w-full max-w-md rounded-2xl border p-6 shadow-2xl ${modal}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-500/20">
          <AlertTriangle className="h-7 w-7 text-red-500" />
        </div>

        {/* Title */}
        <h3 className="mb-2 text-center text-lg font-semibold">
          Delete Scenario
        </h3>

        {/* Message */}
        <p className={`mb-6 text-center text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
          Are you sure you want to delete{" "}
          <span className="font-semibold text-red-400">"{scenarioName}"</span>?
          <br />
          This action cannot be undone.
        </p>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className={`flex-1 rounded-xl border px-4 py-2.5 text-sm font-medium transition ${
              isDarkMode
                ? "border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700"
                : "border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function isScenarioSimulating(s) {
  if (isScenarioPast(s)) return false;
  if (s.status === "active" || s.status === "queued" || s.status === "running") return true;
  if (s.scenario?.is_active) {
    const activeUntil = s.scenario?.active_until;
    if (activeUntil) {
      const targetDate = new Date(activeUntil);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      targetDate.setHours(0, 0, 0, 0);
      return targetDate >= today;
    }
    return true;
  }
  return false;
}

function isScenarioPast(s) {
  if (s.scenario?.is_past) return true;
  const activeUntil = s.scenario?.active_until;
  if (activeUntil) {
    const targetDate = new Date(activeUntil);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    targetDate.setHours(0, 0, 0, 0);
    return targetDate < today;
  }
  return false;
}

function ScenarioTab({ s, active, isDarkMode, tabBase, tabStyle, onSelect, onDuplicate, onDelete, onClose, unsavedChanges, scenarios, handleDeleteClick }) {
  const isSaved = !!s.scenario?.microcycle_template_id;
  const hasUnsaved = unsavedChanges[s.key] === true;
  const isSimulating = isScenarioSimulating(s);
  const isPast = isScenarioPast(s);
  const isLocked = isSimulating || isPast || s.scenario?.locked;
  const progress = s.runProgress ?? 0;
  const computingDone = s.computingDone ?? false;
  const isComputing = isSimulating && !computingDone;

  const activeUntilLabel = (() => {
    if (!isSimulating && !isPast) return null;
    const activeUntil = s.scenario?.active_until;
    if (activeUntil) {
      return formatLocalDate(activeUntil, { locale: 'fr-FR', day: 'numeric', month: 'short' });
    }
    return null;
  })();

  return (
    <button
      key={s.key}
      onClick={() => onSelect(s.key)}
      className={`${tabBase} ${tabStyle}`}
    >
      {/* Simulating indicator */}
      {isSimulating && computingDone && (
        <CheckCircle2
          size={14}
          className={isDarkMode ? "text-emerald-300" : "text-emerald-500"}
          title="Simulation computed — results available"
        />
      )}
      {isSimulating && !computingDone && (
        <Loader2
          size={14}
          className={`animate-spin ${isDarkMode ? "text-blue-300" : "text-blue-500"}`}
          title={`Computing… ${Math.round(progress * 100)}%`}
        />
      )}
      {isPast && !isSimulating && (
        <History
          size={14}
          className={isDarkMode ? "text-gray-400" : "text-gray-500"}
          title="Past scenario"
        />
      )}
      {isLocked && !isSimulating && !isPast && (
        <Lock
          size={14}
          className="text-blue-300"
          title="Scenario locked"
        />
      )}

      {/* Unsaved changes indicator */}
      {hasUnsaved && !isLocked && (
        <Circle
          size={8}
          className="fill-orange-500 text-orange-500 animate-pulse"
          title="Unsaved changes"
        />
      )}

      {/* Saved indicator */}
      {!isSimulating && (
        isSaved ? (
          <Cloud size={14} className="text-emerald-400" title="Saved in DB" />
        ) : (
          <CloudOff size={14} className="text-gray-400" title="Not saved" />
        )
      )}

      <span className="truncate max-w-[140px]">{s.scenario?.name || "Scenario"}</span>

      {isSimulating && activeUntilLabel && (
        <span className={`text-xs font-normal ${isDarkMode ? "text-blue-300" : "text-blue-600"}`}>
          → {activeUntilLabel}
        </span>
      )}

      {/* Progress bar for computing scenarios */}
      {isComputing && (
        <div className={`w-16 h-1.5 rounded-full overflow-hidden ${isDarkMode ? "bg-blue-900/50" : "bg-blue-100"}`}
             title={`${Math.round(progress * 100)}%`}>
          <div
            className="h-full rounded-full bg-blue-500 transition-all duration-700 ease-out"
            style={{ width: `${Math.max(2, Math.round(progress * 100))}%` }}
          />
        </div>
      )}

      {/* Done badge for computed scenarios */}
      {isSimulating && computingDone && (
        <Tag isDarkMode={isDarkMode} tone="emerald">
          done
        </Tag>
      )}

      {isPast && activeUntilLabel && !isSimulating && (
        <span className={`text-xs font-normal ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
          {activeUntilLabel}
        </span>
      )}

      {!isSimulating && !isPast && (
        <Tag isDarkMode={isDarkMode} tone={active ? "emerald" : "zinc"}>
          {s.status}
        </Tag>
      )}
      {isPast && !isSimulating && (
        <Tag isDarkMode={isDarkMode} tone="zinc">
          past
        </Tag>
      )}

      {/* Active underline */}
      {active && !isSimulating && !isPast && (
        <span className="absolute inset-x-2 -bottom-1 h-1 rounded-full bg-emerald-500" />
      )}
      {active && isSimulating && (
        <span className="absolute inset-x-2 -bottom-1 h-1 rounded-full bg-blue-500" />
      )}
      {active && isPast && !isSimulating && (
        <span className="absolute inset-x-2 -bottom-1 h-1 rounded-full bg-gray-500" />
      )}

      <span className="ml-2 flex gap-1">
        {!isLocked && (
          <Copy
            size={14}
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate(s.key);
            }}
            title="Duplicate"
          />
        )}
        {isSaved && onDelete && !isLocked && (
          <Trash2
            size={14}
            className="text-red-400 hover:text-red-300"
            onClick={(e) => handleDeleteClick(e, s.key, s.scenario?.name || "Scenario")}
            title="Delete from database"
          />
        )}
        {scenarios.length > 1 && !isLocked && (
          <X
            size={14}
            onClick={(e) => {
              e.stopPropagation();
              onClose(s.key);
            }}
            title="Close tab"
          />
        )}
      </span>
    </button>
  );
}

export default function ScenarioTabs({
  scenarios,
  activeKey,
  onSelect,
  onAdd,
  onClose,
  onDelete,
  onDuplicate,
  maxScenarios = 20,
  maxSimulated = 3,
  isDarkMode,
  unsavedChanges = {},
}) {
  const [deleteModal, setDeleteModal] = useState({ open: false, key: null, name: "" });

  const tabBase =
    "relative inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold border transition";

  const tabActive = isDarkMode
    ? "bg-emerald-600 border-emerald-500 text-white shadow-lg"
    : "bg-emerald-100 border-emerald-400 text-emerald-900 shadow-md";

  const tabSimulating = isDarkMode
    ? "bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-500/20"
    : "bg-blue-100 border-blue-400 text-blue-900 shadow-md";

  const tabSimulatingSelected = isDarkMode
    ? "bg-blue-700 border-blue-400 text-white shadow-lg shadow-blue-500/30 ring-2 ring-blue-400/50"
    : "bg-blue-200 border-blue-500 text-blue-900 shadow-lg ring-2 ring-blue-400/50";

  const tabIdle = isDarkMode
    ? "bg-gray-950 border-gray-800 text-gray-400 hover:bg-gray-900"
    : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100";

  const tabPast = isDarkMode
    ? "bg-gray-800/60 border-gray-700 text-gray-500 hover:bg-gray-800"
    : "bg-gray-100 border-gray-300 text-gray-400 hover:bg-gray-50";

  const tabPastSelected = isDarkMode
    ? "bg-gray-700 border-gray-600 text-gray-300 shadow-md ring-2 ring-gray-500/40"
    : "bg-gray-200 border-gray-400 text-gray-700 shadow-md ring-2 ring-gray-400/40";

  // Split scenarios into simulating (blue), past (gray), and editable (green)
  const simulatingScenarios = scenarios.filter((s) => isScenarioSimulating(s));
  const pastScenarios = scenarios.filter((s) => !isScenarioSimulating(s) && isScenarioPast(s));
  const editableScenarios = scenarios.filter((s) => !isScenarioSimulating(s) && !isScenarioPast(s));

  const handleDeleteClick = (e, key, name) => {
    e.stopPropagation();
    setDeleteModal({ open: true, key, name });
  };

  const confirmDelete = () => {
    if (deleteModal.key && onDelete) {
      onDelete(deleteModal.key);
    }
    setDeleteModal({ open: false, key: null, name: "" });
  };

  const cancelDelete = () => {
    setDeleteModal({ open: false, key: null, name: "" });
  };

  const getTabStyle = (s) => {
    const active = s.key === activeKey;
    const simulating = isScenarioSimulating(s);
    const past = isScenarioPast(s);
    if (simulating) return active ? tabSimulatingSelected : tabSimulating;
    if (past) return active ? tabPastSelected : tabPast;
    if (active) return tabActive;
    return tabIdle;
  };

  return (
    <>
      <DeleteConfirmModal
        isOpen={deleteModal.open}
        scenarioName={deleteModal.name}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        isDarkMode={isDarkMode}
      />

      <div className="rounded-2xl border p-3 space-y-3">
        {/* Simulating scenarios (blue section, max 3) */}
        {simulatingScenarios.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className={`flex items-center gap-1.5 text-xs font-medium ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}>
                <ShieldCheck size={14} />
                Simulations ({simulatingScenarios.filter(s => s.computingDone).length}/{simulatingScenarios.length} computed)
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {simulatingScenarios.map((s) => (
                <ScenarioTab
                  key={s.key}
                  s={s}
                  active={s.key === activeKey}
                  isDarkMode={isDarkMode}
                  tabBase={tabBase}
                  tabStyle={getTabStyle(s)}
                  onSelect={onSelect}
                  onDuplicate={onDuplicate}
                  onDelete={onDelete}
                  onClose={onClose}
                  unsavedChanges={unsavedChanges}
                  scenarios={scenarios}
                  handleDeleteClick={handleDeleteClick}
                />
              ))}
            </div>
          </div>
        )}

        {/* Separator if both sections have items */}
        {simulatingScenarios.length > 0 && editableScenarios.length > 0 && (
          <div className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`} />
        )}

        {/* Editable scenarios (green section, unlimited) */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold">Scenarios</h2>
            <div className="flex items-center gap-2">
              <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                {editableScenarios.length} scenario{editableScenarios.length > 1 ? "s" : ""}
              </span>
              <button
                onClick={onAdd}
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition"
              >
                <Plus size={16} />
                Add
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {editableScenarios.map((s) => (
              <ScenarioTab
                key={s.key}
                s={s}
                active={s.key === activeKey}
                isDarkMode={isDarkMode}
                tabBase={tabBase}
                tabStyle={getTabStyle(s)}
                onSelect={onSelect}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
                onClose={onClose}
                unsavedChanges={unsavedChanges}
                scenarios={scenarios}
                handleDeleteClick={handleDeleteClick}
              />
            ))}
            {editableScenarios.length === 0 && (
              <p className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                No editable scenario. Add one with the "Add" button.
              </p>
            )}
          </div>
        </div>

        {/* Separator before past section */}
        {pastScenarios.length > 0 && (editableScenarios.length > 0 || simulatingScenarios.length > 0) && (
          <div className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`} />
        )}

        {/* Past scenarios (gray section, max 5) */}
        {pastScenarios.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className={`flex items-center gap-1.5 text-xs font-medium ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                <History size={14} />
                Past scenarios ({pastScenarios.length})
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {pastScenarios.map((s) => (
                <ScenarioTab
                  key={s.key}
                  s={s}
                  active={s.key === activeKey}
                  isDarkMode={isDarkMode}
                  tabBase={tabBase}
                  tabStyle={getTabStyle(s)}
                  onSelect={onSelect}
                  onDuplicate={onDuplicate}
                  onDelete={onDelete}
                  onClose={onClose}
                  unsavedChanges={unsavedChanges}
                  scenarios={scenarios}
                  handleDeleteClick={handleDeleteClick}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
